import React, { useEffect, useState } from 'react'
import AdminHeader from '../components/AdminHeader'
import { Alert } from '@mui/material';
import { AnnouncementService } from '../services/AnnouncementService';

const AnnouncementStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await AnnouncementService.getAnnouncementStats();
            setStats(response);
        } catch (err) {
            setError('Failed to fetch statistics');
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, subtitle }) => (
        <div className="bg-white p-6 rounded-lg shadow-md border">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
            <p className="text-3xl font-bold text-blue-600 mb-1">{value}</p>
            {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
        </div>
    );

    const TypeStatsChart = ({ data }) => (
        <div className="bg-white p-6 rounded-lg shadow-md border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">By Type</h3>
            <div className="space-y-2">
                {data.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                        <span className="capitalize text-gray-700">{item._id}</span>
                        <span className="font-semibold text-gray-900">{item.count}</span>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="flex w-full h-screen overflow-auto">
            <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
                <div className="px-10 bg-white">
                    <AdminHeader
                        heading={"Announcement Statistics"}
                        pages={"Pages / Announcements / Statistics"}
                    />
                </div>

                {error &&
                    <Alert
                        severity="error"
                        sx={{
                            position: "fixed",
                            width: "90%",
                            margin: "auto",
                            top: 20,
                            left: 0,
                            right: 0,
                            zIndex: 10,
                        }}
                    >
                        {error}
                    </Alert>
                }

                <div className="px-6 py-5 h-full overflow-auto">
                    <div className="m-auto shadow-4xl pb-[30px] bg-white my-7 rounded-xl w-full tablet:w-[90%] gap-x-5 min-h-[80%]">
                        <div className="w-[95%] m-auto flex flex-col">
                            <div className="flex justify-between items-center mt-3 mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">Announcement Overview</h2>
                                <button
                                    onClick={fetchStats}
                                    className="px-4 py-2 bg-blue text-white rounded-md hover:bg-blue-700"
                                >
                                    Refresh
                                </button>
                            </div>

                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                                </div>
                            ) : stats ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                    <StatCard
                                        title="Total Announcements"
                                        value={stats.overview.total}
                                        subtitle="All time"
                                    />
                                    <StatCard
                                        title="Active Announcements"
                                        value={stats.overview.active}
                                        subtitle="Currently visible"
                                    />
                                    <StatCard
                                        title="Inactive Announcements"
                                        value={stats.overview.total - stats.overview.active}
                                        subtitle="Hidden or expired"
                                    />
                                </div>
                            ) : null}

                            {stats && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <TypeStatsChart data={stats.byType} />
                                    <div className="bg-white p-6 rounded-lg shadow-md border">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">By Priority</h3>
                                        <div className="space-y-2">
                                            {stats.byPriority.map((item, index) => (
                                                <div key={index} className="flex justify-between items-center">
                                                    <span className="capitalize text-gray-700">{item._id}</span>
                                                    <span className="font-semibold text-gray-900">{item.count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-md border">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">By Audience</h3>
                                        <div className="space-y-2">
                                            {stats.byAudience.map((item, index) => (
                                                <div key={index} className="flex justify-between items-center">
                                                    <span className="capitalize text-gray-700">{item._id}</span>
                                                    <span className="font-semibold text-gray-900">{item.count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnnouncementStats;
